<template>
    <div class="send-message-container">
        <el-steps :active="currentStep" align-center style="margin-bottom: 30px;">
            <el-step title="选择模板" />
            <el-step title="选择用户" />
            <el-step title="填写内容" />
            <el-step title="确认发送" />
        </el-steps>

        <!-- 步骤1: 选择模板 -->
        <div v-if="currentStep === 1" class="step-container">
            <h3>选择消息模板</h3>
            <WechatTemplateView :account-id="accountId" :store-id="storeId"
                @template-selected="handleTemplateSelected" />
        </div>

        <!-- 步骤2: 选择用户 -->
        <div v-if="currentStep === 2" class="step-container">
            <h3>选择接收用户</h3>

            <div class="user-selection">
                <div class="selection-header">
                    <el-radio-group v-model="selectionMode" @change="handleSelectionModeChange">
                        <el-radio-button label="manual">手动选择</el-radio-button>
                        <el-radio-button label="search">搜索选择</el-radio-button>
                        <el-radio-button label="tag">按标签选择</el-radio-button>
                    </el-radio-group>

                    <div class="selection-stats">
                        已选择: {{ selectedUsers.length }} 个用户
                        <el-button type="text" @click="clearSelection"
                            :disabled="selectedUsers.length === 0">清空</el-button>
                    </div>
                </div>

                <!-- 手动选择模式 -->
                <!-- 手动选择模式 -->
                <div v-if="selectionMode === 'manual'" class="selection-content">
                    <WechatUserView :account-id="accountId" :store-id="storeId" @user-selected="handleUserSelected"
                        @users-selected="handleUsersSelected" />
                </div>

                <!-- 搜索选择模式 -->
                <div v-if="selectionMode === 'search'" class="selection-content">
                    <div class="search-controls">
                        <el-input v-model="searchKeyword" placeholder="输入OpenID、备注、UnionID进行搜索"
                            style="width: 400px; margin-right: 15px" @keyup.enter="searchUsers">
                            <template #prefix>
                                <el-icon>
                                    <Search />
                                </el-icon>
                            </template>
                        </el-input>
                        <el-button type="primary" @click="searchUsers">搜索</el-button>
                        <el-button @click="resetSearch">重置</el-button>
                    </div>

                    <div class="search-results">
                        <el-table ref="searchTable" :data="searchResults" stripe height="400" v-loading="searchLoading"
                            @selection-change="handleSearchSelectionChange">
                            <el-table-column type="selection" width="55" />
                            <el-table-column prop="openid" label="OpenID" />
                            <el-table-column prop="remark" label="备注" width="120" />
                            <el-table-column prop="unionid" label="UnionID" width="220" />
                            <el-table-column prop="subscribe_scene" label="关注渠道" width="120">
                                <template #default="{ row }">
                                    <el-tag size="small">{{ getSceneLabel(row.subscribe_scene) }}</el-tag>
                                </template>
                            </el-table-column>
                        </el-table>

                        <!-- 搜索分页 -->
                        <div class="pagination-container" v-if="searchPagination.total > 0">
                            <el-pagination v-model:current-page="searchPagination.page"
                                v-model:page-size="searchPagination.pageSize" :total="searchPagination.total"
                                :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                                @size-change="handleSearchPageChange" @current-change="handleSearchPageChange" />
                        </div>
                    </div>
                </div>

                <!-- 标签选择模式 -->
                <div v-if="selectionMode === 'tag'" class="selection-content">
                    <div class="tag-selection">
                        <h4>选择标签</h4>
                        <el-checkbox-group v-model="selectedTags" @change="handleTagSelectionChange">
                            <div class="tag-checkbox-group">
                                <el-checkbox v-for="tag in tags" :key="tag.tag_id" :label="tag.tag_id"
                                    class="tag-checkbox">
                                    {{ tag.tag_name }} ({{ tag.user_count }})
                                </el-checkbox>
                            </div>
                        </el-checkbox-group>
                    </div>

                    <div class="tag-users">
                        <h4>标签用户列表 ({{ tagUsers.length }} 人)</h4>
                        <el-table ref="tagTable" :data="tagUsers" stripe height="300" v-loading="tagUsersLoading"
                            @selection-change="handleTagUsersSelectionChange">
                            <el-table-column type="selection" width="55" />
                            <el-table-column prop="openid" label="OpenID" />
                            <el-table-column prop="remark" label="备注" width="120" />
                            <el-table-column prop="subscribe_scene" label="关注渠道" width="120">
                                <template #default="{ row }">
                                    <el-tag size="small">{{ getSceneLabel(row.subscribe_scene) }}</el-tag>
                                </template>
                            </el-table-column>
                        </el-table>

                        <!-- 标签用户分页 -->
                        <div class="pagination-container" v-if="tagPagination.total > 0">
                            <el-pagination v-model:current-page="tagPagination.page"
                                v-model:page-size="tagPagination.pageSize" :total="tagPagination.total"
                                :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                                @size-change="handleTagPageChange" @current-change="handleTagPageChange" />
                        </div>
                    </div>
                </div>

                <!-- 已选用户列表 -->
                <div class="selected-users" v-if="selectedUsers.length > 0">
                    <h4>已选用户列表</h4>
                    <el-table :data="selectedUsers" stripe height="200">
                        <el-table-column prop="openid" label="OpenID" />
                        <el-table-column prop="remark" label="备注" width="120" />
                        <el-table-column prop="unionid" label="UnionID" width="220" />
                        <el-table-column label="操作" width="80">
                            <template #default="{ $index }">
                                <el-button type="text" size="small" @click="removeUser($index)">移除</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>

        <!-- 步骤3: 填写内容 -->
        <div v-if="currentStep === 3" class="step-container">
            <h3>填写模板内容</h3>

            <div class="template-preview">
                <h4>模板预览: {{ selectedTemplate.title }}</h4>
                <div class="preview-content">
                    <div v-html="formatTemplatePreview(selectedTemplate.content, form.keywords)"></div>
                </div>
            </div>

            <el-form :model="form" label-width="120px" class="content-form" ref="contentForm">
                <el-form-item v-for="keyword in templateKeywords" :key="keyword" :label="keyword"
                    :prop="`keywords.${keyword}`"
                    :rules="[{ required: true, message: `请输入${keyword}`, trigger: 'blur' }]">

                    <!-- 字符串类型字段 -->
                    <el-input v-if="isStringField(keyword)" v-model="form.keywords[keyword]"
                        :placeholder="`请输入${keyword}`" @focus="autoGenerateString(keyword)">
                        <template #append>
                            <el-button @click="autoGenerateString(keyword)" title="自动生成">
                                <el-icon>
                                    <Refresh />
                                </el-icon>
                            </el-button>
                        </template>
                    </el-input>

                    <!-- 时间类型字段 -->
                    <el-date-picker v-else-if="isTimeField(keyword)" v-model="form.keywords[keyword]" type="datetime"
                        placeholder="选择日期时间" format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss"
                        @change="handleTimeChange(keyword)" />

                    <!-- 默认文本输入框 -->
                    <el-input v-else v-model="form.keywords[keyword]" :placeholder="`请输入${keyword}`" />
                </el-form-item>

                <el-form-item label="跳转链接">
                    <el-input v-model="form.url" placeholder="请输入跳转链接（可选）" />
                </el-form-item>
            </el-form>
        </div>

        <!-- 步骤4: 确认发送 -->
        <div v-if="currentStep === 4" class="step-container">
            <h3>确认发送</h3>

            <el-alert type="info" show-icon style="margin-bottom: 20px;">
                即将向 {{ selectedUsers.length }} 个用户发送模板消息，请确认信息无误
            </el-alert>

            <div class="confirm-info">
                <el-descriptions title="发送信息" border :column="2">
                    <el-descriptions-item label="模板标题">{{ selectedTemplate.title }}</el-descriptions-item>
                    <el-descriptions-item label="接收用户">{{ selectedUsers.length }} 人</el-descriptions-item>
                    <el-descriptions-item label="跳转链接">{{ form.url || '无' }}</el-descriptions-item>
                    <el-descriptions-item label="模板ID">{{ selectedTemplate.template_id }}</el-descriptions-item>
                </el-descriptions>

                <div class="keywords-preview" style="margin-top: 20px;">
                    <h4>消息内容预览</h4>
                    <div class="preview-box">
                        <div v-html="formatTemplatePreview(selectedTemplate.content, form.keywords)"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 步骤导航 -->
        <div class="step-actions">
            <el-button @click="previousStep" :disabled="currentStep === 1">上一步</el-button>
            <el-button type="primary" @click="nextStep" :loading="sending" :disabled="!canProceed">
                {{ currentStep === 4 ? '确认发送' : '下一步' }}
            </el-button>
        </div>

        <!-- 发送结果对话框 -->
        <el-dialog v-model="resultDialogVisible" title="发送结果" width="600px">
            <div v-if="sendResult">
                <el-alert :type="sendResult.success_count > 0 ? 'success' : 'error'" show-icon>
                    <span>成功: {{ sendResult.success_count }} 条，失败: {{ sendResult.fail_count }} 条</span>
                </el-alert>

                <div v-if="sendResult.fail_list && sendResult.fail_list.length > 0" style="margin-top: 20px;">
                    <h4>失败详情:</h4>
                    <el-table :data="sendResult.fail_list" stripe height="200">
                        <el-table-column prop="openid" label="OpenID" width="240" />
                        <el-table-column prop="errmsg" label="错误信息" />
                    </el-table>
                </div>
            </div>

            <template #footer>
                <el-button @click="resultDialogVisible = false">关闭</el-button>
                <el-button type="primary" @click="restartProcess">重新发送</el-button>
            </template>
        </el-dialog>
    </div>

    <!-- 在搜索结果表格后添加空状态 -->
    <div v-if="!searchLoading && searchResults.length === 0 && searchKeyword" class="empty-state">
        <el-empty description="暂无搜索结果">
            <el-button @click="resetSearch">重新搜索</el-button>
        </el-empty>
    </div>

    <!-- 在标签用户表格后添加空状态 -->
    <div v-if="!tagUsersLoading && tagUsers.length === 0 && selectedTags.length > 0" class="empty-state">
        <el-empty description="该标签下暂无用户">
            <el-button @click="selectedTags = []">选择其他标签</el-button>
        </el-empty>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import WechatTemplateView from './WechatTemplateView.vue'
import WechatUserView from './WechatUserView.vue'
import { Search } from '@element-plus/icons-vue'
import { Refresh } from '@element-plus/icons-vue'

export default {
    name: 'WechatSendMessage',
    components: { WechatTemplateView, WechatUserView, Search, Refresh },
    props: {
        accountId: {
            type: Number,
            required: true
        },
        storeId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            currentStep: 1,
            sending: false,
            selectionMode: 'manual',
            selectedTemplate: null,
            selectedUsers: [],
            searchKeyword: '',
            searchResults: [],
            searchLoading: false,
            tags: [],
            selectedTags: [],
            tagUsers: [],
            tagUsersLoading: false,
            form: {
                keywords: {},
                url: ''
            },
            sendResult: null,
            resultDialogVisible: false,
            searchPagination: {
                page: 1,
                pageSize: 20,
                total: 0
            },
            tagPagination: {
                page: 1,
                pageSize: 20,
                total: 0
            },
            subscribeScenes: [
                { value: 'ADD_SCENE_SEARCH', label: '公众号搜索' },
                { value: 'ADD_SCENE_ACCOUNT_MIGRATION', label: '公众号迁移' },
                { value: 'ADD_SCENE_PROFILE_CARD', label: '名片分享' },
                { value: 'ADD_SCENE_QR_CODE', label: '扫描二维码' },
                { value: 'ADD_SCENE_PROFILE_LINK', label: '图文页内名称点击' },
                { value: 'ADD_SCENE_PROFILE_ITEM', label: '图文页右上角菜单' },
                { value: 'ADD_SCENE_PAID', label: '支付后关注' },
                { value: 'ADD_SCENE_WECHAT_ADVERTISEMENT', label: '微信广告' },
                { value: 'ADD_SCENE_REPRINT', label: '他人转载' },
                { value: 'ADD_SCENE_LIVESTREAM', label: '视频号直播' },
                { value: 'ADD_SCENE_CHANNELS', label: '视频号' },
                { value: 'ADD_SCENE_WXA', label: '小程序关注' },
                { value: 'ADD_SCENE_OTHERS', label: '其他' }
            ]
        }
    },
    computed: {
        canProceed() {
            switch (this.currentStep) {
                case 1:
                    return this.selectedTemplate !== null;
                case 2:
                    return this.selectedUsers.length > 0;
                case 3:
                    return this.templateKeywords.every(keyword =>
                        this.form.keywords[keyword] && this.form.keywords[keyword].trim() !== ''
                    );
                case 4:
                    return true;
                default:
                    return false;
            }
        },
        templateKeywords() {
            if (!this.selectedTemplate) return [];
            const content = this.selectedTemplate.content;
            const matches = content.match(/{{(.*?)\.DATA}}/g) || [];
            return [...new Set(matches.map(match => match.replace(/{{(.*?)\.DATA}}/, '$1').trim()))];
        }
    },
    mounted() {
        this.fetchTags();
    },
    methods: {
        // 步骤导航
        nextStep() {
            if (this.currentStep === 4) {
                this.sendMessage();
            } else {
                if (this.currentStep === 3) {
                    this.$refs.contentForm.validate((valid) => {
                        if (valid) {
                            this.currentStep++;
                        }
                    });
                } else {
                    this.currentStep++;
                }
            }
        },

        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },

        // 判断是否为字符串类型字段
        isStringField(keyword) {
            const stringPatterns = ['string', 'character', 'str', 'code', 'no', 'id', 'number'];
            return stringPatterns.some(pattern =>
                keyword.toLowerCase().includes(pattern)
            );
        },

        // 判断是否为时间类型字段
        isTimeField(keyword) {
            const timePatterns = ['time', 'date', 'datetime'];
            return timePatterns.some(pattern =>
                keyword.toLowerCase().includes(pattern)
            );
        },

        // 自动生成字符串
        autoGenerateString(keyword) {
            if (!this.form.keywords[keyword]) {
                const timestamp = new Date().getTime();
                const randomStr = Math.random().toString(36).substr(2, 6).toUpperCase();
                const generatedString = `T${timestamp}${randomStr}`.substr(0, 20); // 限制长度
                this.form.keywords[keyword] = generatedString;
                this.$message.success(`已自动生成${keyword}`);
            }
        },

        // 处理时间选择变化
        handleTimeChange(keyword) {
            if (!this.form.keywords[keyword]) {
                // 如果没有选择时间，自动填充当前时间
                const now = new Date();
                this.form.keywords[keyword] = this.formatDateTime(now);
                this.$message.success(`已自动填充当前时间`);
            }
        },

        // 格式化日期时间
        formatDateTime(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },

        // 模板选择 - 修改：初始化时自动生成内容
        handleTemplateSelected(template) {
            this.selectedTemplate = template;
            // 初始化关键词表单
            this.form.keywords = {};
            this.templateKeywords.forEach(keyword => {
                this.form.keywords[keyword] = '';
                // 根据字段类型自动生成内容
                if (this.isStringField(keyword)) {
                    this.autoGenerateString(keyword);
                } else if (this.isTimeField(keyword)) {
                    this.handleTimeChange(keyword);
                }
            });
            // 自动跳转到填写内容步骤
            this.nextStep();
        },



        // 用户选择
        handleUserSelected(user) {
            if (!this.selectedUsers.find(u => u.openid === user.openid)) {
                this.selectedUsers.push(user);
                this.$message.success(`已选择用户: ${user.remark || user.openid}`);
            }
        },

        // 批量用户选择 - 新增方法
        handleUsersSelected(users) {
            // 清空当前选择
            this.selectedUsers = [];
            // 添加新选择的用户（去重）
            users.forEach(user => {
                if (!this.selectedUsers.find(u => u.openid === user.openid)) {
                    this.selectedUsers.push(user);
                }
            });
            this.$message.success(`已批量选择 ${this.selectedUsers.length} 个用户`);
            // 自动跳转到下一步
            if (this.currentStep === 1) {
                this.currentStep = 2;
            }
        },

        // 同步所有表格的选中状态
        syncAllTableSelections() {  // 移除 removedUser 参数
            this.$nextTick(() => {
                // 同步搜索表格
                if (this.$refs.searchTable) {
                    this.searchResults.forEach(row => {
                        const isSelected = this.selectedUsers.some(user => user.openid === row.openid);
                        this.$refs.searchTable.toggleRowSelection(row, isSelected);
                    });
                }

                // 同步标签表格
                if (this.$refs.tagTable) {
                    this.tagUsers.forEach(row => {
                        const isSelected = this.selectedUsers.some(user => user.openid === row.openid);
                        this.$refs.tagTable.toggleRowSelection(row, isSelected);
                    });
                }
            });
        },

        // 修改移除用户的方法
        removeUser(index) {
            this.selectedUsers.splice(index, 1);
            // 同步更新其他表格的选中状态
            this.syncAllTableSelections();  // 移除参数
        },

        // 清空选择
        clearSelection() {
            this.selectedUsers = [];
        },

        // 修改选择模式变化处理
        handleSelectionModeChange() {
            // 不清空已选用户，只重置数据
            this.searchResults = [];
            this.tagUsers = [];
            this.selectedTags = [];
            this.searchKeyword = '';
            this.searchPagination.page = 1;
            this.tagPagination.page = 1;

            // 根据模式加载数据
            if (this.selectionMode === 'search') {
                // 如果有搜索关键词，自动搜索
                if (this.searchKeyword) {
                    this.searchUsers();
                }
            } else if (this.selectionMode === 'tag') {
                // 如果有选中的标签，自动加载用户
                if (this.selectedTags.length > 0) {
                    this.updateTagUsers();
                }
            }
        },

        // 重置搜索
        resetSearch() {
            this.searchKeyword = '';
            this.searchResults = [];
            this.searchPagination.page = 1;
            this.searchPagination.total = 0;
        },

        // 修改搜索选择变化
        handleSearchSelectionChange(selection) {
            // 合并选择，避免重复
            const newSelectedUsers = [...this.selectedUsers];

            selection.forEach(user => {
                if (!newSelectedUsers.find(u => u.openid === user.openid)) {
                    newSelectedUsers.push(user);
                }
            });

            this.selectedUsers = newSelectedUsers;
        },

        // 搜索分页变化
        handleSearchPageChange() {
            this.searchUsers();
        },

        // 标签选择变化
        handleTagSelectionChange() {
            this.updateTagUsers();
        },

        // 修改搜索用户方法
        async searchUsers() {
            if (!this.searchKeyword.trim()) {
                this.$message.warning('请输入搜索关键词');
                return;
            }

            this.searchLoading = true;
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/wechat_account.php`,
                    {
                        action: 'search_users',
                        account_id: this.accountId,
                        store_id: this.storeId,
                        keyword: this.searchKeyword,
                        page: this.searchPagination.page,
                        page_size: this.searchPagination.pageSize
                    }
                );

                if (data.success) {
                    this.searchResults = data.data.user_list || [];
                    this.searchPagination.total = data.pagination?.total || 0;

                    // 同步选择状态
                    this.syncSearchSelection();
                } else {
                    this.$message.error(data.error || '搜索失败');
                }
            } catch (error) {
                console.error('搜索用户错误:', error);
                this.$message.error('搜索用户失败');
            } finally {
                this.searchLoading = false;
            }
        },

        // 同步搜索结果的选中状态
        syncSearchSelection() {
            this.$nextTick(() => {
                if (this.$refs.searchTable) {
                    this.searchResults.forEach(row => {
                        const isSelected = this.selectedUsers.some(user => user.openid === row.openid);
                        this.$refs.searchTable.toggleRowSelection(row, isSelected);
                    });
                }
            });
        },


        // 修改标签用户获取方法
        async updateTagUsers() {
            if (this.selectedTags.length === 0) {
                this.tagUsers = [];
                this.tagPagination.total = 0;
                return;
            }

            this.tagUsersLoading = true;
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/wechat_account.php`,
                    {
                        action: 'get_users_by_tags',
                        account_id: this.accountId,
                        store_id: this.storeId,
                        tag_ids: this.selectedTags,
                        page: this.tagPagination.page,
                        page_size: this.tagPagination.pageSize
                    }
                );

                if (data.success) {
                    this.tagUsers = data.data.user_list || [];
                    this.tagPagination.total = data.pagination?.total || 0;

                    // 同步选择状态
                    this.syncTagSelection();
                } else {
                    this.$message.error(data.error || '获取标签用户失败');
                }
            } catch (error) {
                console.error('获取标签用户错误:', error);
                this.$message.error('获取标签用户失败');
            } finally {
                this.tagUsersLoading = false;
            }
        },

        // 同步标签用户的选中状态
        syncTagSelection() {
            this.$nextTick(() => {
                if (this.$refs.tagTable) {
                    this.tagUsers.forEach(row => {
                        const isSelected = this.selectedUsers.some(user => user.openid === row.openid);
                        this.$refs.tagTable.toggleRowSelection(row, isSelected);
                    });
                }
            });
        },

        // 修改标签用户选择变化
        handleTagUsersSelectionChange(selection) {
            // 合并选择，避免重复
            const newSelectedUsers = [...this.selectedUsers];

            selection.forEach(user => {
                if (!newSelectedUsers.find(u => u.openid === user.openid)) {
                    newSelectedUsers.push(user);
                }
            });

            this.selectedUsers = newSelectedUsers;
        },

        // 标签分页变化
        handleTagPageChange() {
            this.updateTagUsers();
        },

        // 获取标签
        async fetchTags() {
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/wechat_account.php`,
                    {
                        action: 'get_tags',
                        account_id: this.accountId,
                        store_id: this.storeId
                    }
                );

                if (data.success) {
                    this.tags = data.data.tags || [];
                }
            } catch (error) {
                console.error('获取标签错误:', error);
            }
        },

        // 格式化模板预览
        formatTemplatePreview(content, keywords) {
            let formatted = content;
            Object.keys(keywords).forEach(key => {
                const value = keywords[key] || `{${key}}`;
                formatted = formatted.replace(new RegExp(`{{${key}\\.DATA}}`, 'g'),
                    `<span class="keyword-value">${value}</span>`);
            });
            return formatted.replace(/\n/g, '<br>');
        },

        // 获取场景标签
        getSceneLabel(scene) {
            const sceneObj = this.subscribeScenes.find(s => s.value === scene);
            return sceneObj ? sceneObj.label : scene;
        },

        // 发送消息
        async sendMessage() {
            this.sending = true;
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/wechat_account.php`,
                    {
                        action: 'send_message',
                        account_id: this.accountId,
                        store_id: this.storeId,
                        template_id: this.selectedTemplate.template_id,
                        openids: JSON.stringify(this.selectedUsers.map(u => u.openid)),
                        keywords: JSON.stringify(this.form.keywords),
                        url: this.form.url
                    }
                );

                if (data.success) {
                    this.sendResult = data.data;
                    this.resultDialogVisible = true;
                    this.$message.success(`消息发送完成，成功 ${data.data.success_count} 条`);
                } else {
                    this.$message.error(data.error || '发送失败');
                }
            } catch (error) {
                this.$message.error('发送失败');
                console.error('发送消息错误:', error);
            } finally {
                this.sending = false;
            }
        },

        // 重新开始流程
        restartProcess() {
            this.currentStep = 1;
            this.selectedTemplate = null;
            this.selectedUsers = [];
            this.form = { keywords: {}, url: '' };
            this.resultDialogVisible = false;
            this.sendResult = null;
            this.selectionMode = 'manual';
            this.searchKeyword = '';
            this.searchResults = [];
            this.selectedTags = [];
            this.tagUsers = [];
        }
    }
}
</script>

<style scoped>
.send-message-container {
    padding: 20px;
}

.step-container {
    min-height: 400px;
    padding: 20px;
    background: #f8fafc;
    border-radius: 8px;
    margin-bottom: 20px;
}

.user-selection {
    background: white;
    padding: 20px;
    border-radius: 8px;
}

.selection-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebeef5;
}

.selection-stats {
    color: #666;
    font-size: 14px;
}

.selection-content {
    margin-bottom: 20px;
}

.search-controls {
    margin-bottom: 15px;
}

.tag-selection {
    margin-bottom: 20px;
}

.tag-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
}

.tag-checkbox {
    margin-right: 15px;
    margin-bottom: 10px;
}

.selected-users {
    margin-top: 20px;
}

.template-preview {
    background: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.preview-content {
    padding: 15px;
    background: #f5f7fa;
    border-radius: 4px;
    border-left: 4px solid #409eff;
}

.content-form {
    background: white;
    padding: 20px;
    border-radius: 8px;
}

.confirm-info {
    background: white;
    padding: 20px;
    border-radius: 8px;
}

.preview-box {
    padding: 15px;
    background: #f5f7fa;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
}

.step-actions {
    text-align: center;
    margin-top: 30px;
}

.pagination-container {
    margin-top: 15px;
    display: flex;
    justify-content: center;
}

.keyword-value {
    color: #409eff;
    font-weight: bold;
    background: #ecf5ff;
    padding: 2px 6px;
    border-radius: 3px;
}

.empty-state {
    padding: 40px 0;
    text-align: center;
    background: #fafafa;
    border-radius: 4px;
    margin: 10px 0;
}
</style>